import { OrgFeature, TransactionCardScheme } from '../enums';
import { Order, Org, TenderOption } from '../models';
import { TenderData, TenderMapData } from '../models/concession/tender';

export default class PaymentHelper {
  static getStripeCardSchemeFromStripeCard(stripeCard: any): TransactionCardScheme {
    const brand = stripeCard?.brand?.toLowerCase();
    switch (brand) {
      case 'amex':
      case 'american express':
        return TransactionCardScheme.AMEX;
      case 'visa':
        return TransactionCardScheme.VISA;
      case 'mastercard':
        return TransactionCardScheme.MASTERCARD;
      case 'discover':
        return TransactionCardScheme.DISCOVER;
      case 'diners':
        return TransactionCardScheme.DINERS;
      case 'maestro':
        return TransactionCardScheme.MAESTRO;
      case 'jcb':
        return TransactionCardScheme.JCB;
    }
    return TransactionCardScheme.OTHER;
  }

  static getStripeCardSchemeFromStripePaymentIntent(paymentIntent: any): TransactionCardScheme {
    const charge = paymentIntent?.charges?.data[0];
    const card = charge?.payment_method_details?.card_present;
    return PaymentHelper.getStripeCardSchemeFromStripeCard(card);
  }

  static getNameOnCardFromStripePaymentMethod(paymentMethod: any): string {
    const card =
      paymentMethod?.card || paymentMethod?.charges?.data[0]?.payment_method_details?.card_present;
    return (
      card?.generated_from?.payment_method_details?.card_present?.cardholder_name ||
      card?.payment_method_details?.card_present?.cardholder_name ||
      card?.cardholder_name
    );
  }

  /**
   * @param order Order
   * @param org Org
   * @param hasGiftCard Whether gift card tender is available
   * @param options Optional. hiddenTenderKeys: tender keys to hide from POS (e.g. integration-only). showOnPos is respected per option when set.
   */
  static getTenderOptionsForOrder(
    order: Order,
    org: Org,
    hasGiftCard: boolean,
    options?: { hiddenTenderKeys?: string[] }
  ): TenderOption[] {
    const hiddenKeys = new Set(options?.hiddenTenderKeys ?? []);
    const tenderOptions: TenderOption[] = [
      { key: 'cash', name: 'Cash', icon: 'cash', showOnPos: true },
      { key: 'card', name: 'Credit / Debit', icon: 'card', showOnPos: true },
    ];
    if (hasGiftCard) {
      tenderOptions.push({
        key: 'gift-card',
        name: 'Gift Card',
        icon: 'card-outline',
        showOnPos: true,
      });
    }
    if (!order.data.member) {
      tenderOptions.push({ key: 'split', name: 'Split Payment', icon: 'albums', showOnPos: true });
    }
    if (order.isEvent) {
      tenderOptions.push({ key: 'event', name: 'Event', icon: 'receipt-outline', showOnPos: true });
    }
    if (org?.hasFeature(OrgFeature.MEMBER_CHARGES) && !order.data.transactions?.length) {
      tenderOptions.push({
        key: 'member',
        name: 'Member Charge',
        icon: 'person-add-outline',
        showOnPos: true,
      });
    }
    if (order.data.cardOnFile) {
      tenderOptions.unshift({
        key: 'card-on-file',
        name: `Card On File ${order.data.cardOnFile.cardScheme} ${order.data.cardOnFile.last4}`,
        icon: 'wallet',
        color: 'secondary',
        showOnPos: true,
      });
    }
    if (!order.data.cardOnFile && order.data.guestCardsOnFile?.length) {
      const cardOnFile = order.data.guestCardsOnFile[0];
      tenderOptions.unshift({
        key: 'card-on-file',
        name: `Card On File ${cardOnFile.cardScheme} ${cardOnFile.last4}`,
        icon: 'wallet',
        showOnPos: true,
      });
    }
    return tenderOptions.filter(t => t.showOnPos !== false && !hiddenKeys.has(t.key));
  }

  /**
   * Returns tender option keys to hide on POS when tenderMap entries have showOnPos === false.
   * Use with getTenderOptionsForOrder(..., { hiddenTenderKeys: PaymentHelper.getHiddenTenderKeysFromTenderMap(tenderMap) }).
   */
  static getHiddenTenderKeysFromTenderMap(tenderMap: TenderMapData | null | undefined): string[] {
    if (!tenderMap) {return [];}
    const map: Array<{ k: keyof TenderMapData; optionKey: string }> = [
      { k: 'cash', optionKey: 'cash' },
      { k: 'credit', optionKey: 'card' },
      { k: 'member', optionKey: 'member' },
      { k: 'event', optionKey: 'event' },
      { k: 'giftCard', optionKey: 'gift-card' },
    ];
    return map
      .filter(({ k }) => {
        const v = tenderMap[k];
        const data = typeof v === 'object' && v !== null ? (v as TenderData) : null;
        return data?.showOnPos === false;
      })
      .map(({ optionKey }) => optionKey);
  }

  static createClientId(order: Order): string {
    const transastionCount = order.data.transactions.length || 1;
    return `${order.data.orderNumber}-${transastionCount}`;
  }
}
